import { Stack } from '@mui/material';
import { getAllContacts } from 'lib/api/contacts';
import { Contact } from 'lib/api/contacts/types';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import ContactsTable from 'lib/common/Tables/ContactsTable';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import NewContact from './components/NewContact';

type FormattedContact = Contact & {
  business: { primary: string; secondary: string };
};

const Main: React.FC = () => {
  const dispatch = useDispatch();
  const { data, isPending } = getAllContacts(dispatch);

  const [openModal, setOpenModal] = useState(false);
  const [filteredContacts, setFilteredContacts] = useState<FormattedContact[]>(
    [],
  );
  const [businessFilter, setBusinessFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');

  const contacts = useMemo(() => {
    const mapped = data?.items.map((c) => ({
      ...c,
      business: {
        primary: c.business.name,
        secondary: c.business.identifier,
      },
    }));
    return mapped;
  }, [data]);

  const businessOptions = useMemo(() => {
    return [
      { value: 'all', label: 'Todos' },
      ...[...new Set(contacts?.map((x) => x.business.primary))].map((x) => ({
        value: x,
        label: x,
      })),
    ];
  }, [contacts]);

  const roleOptions = useMemo(() => {
    const roles = [
      ...new Set(
        contacts?.map((x) => x.role).filter((r): r is string => r != null) ??
          [],
      ),
    ];
    return [
      { value: 'all', label: 'Todos' },
      ...roles.map((x) => ({ value: x, label: x })),
    ];
  }, [contacts]);

  const handleSearch = async () => {
    let filtered = contacts?.filter((x) =>
      businessFilter === 'all' ? true : x.business.primary === businessFilter,
    );
    filtered = filtered?.filter((x) =>
      roleFilter === 'all' ? true : x.role === roleFilter,
    );
    setFilteredContacts(filtered ?? []);
  };

  const filters: Filter[] = [
    {
      type: 'select',
      label: 'Empresa',
      value: businessFilter,
      setter: setBusinessFilter,
      options: businessOptions,
    },
    {
      type: 'select',
      label: 'Rol',
      value: roleFilter,
      setter: setRoleFilter,
      options: roleOptions,
    },
  ];

  const buttons: CustomButtonProps[] = [
    {
      name: 'Buscar',
      action: () => {},
      color: 'primary',
      customButton: (
        <GlobalSearcher filters={filters} triggerSearch={handleSearch} />
      ),
    },
    {
      name: 'Nuevo contacto',
      action: () => setOpenModal(true),
      color: 'primary',
    },
  ];

  useEffect(() => {
    setFilteredContacts(contacts ?? []);
  }, [contacts]);

  if (isPending) return <CenteredCircularProgress type="layout" />;

  return (
    <Stack>
      <HeaderWithActions
        type="subHeader"
        variant="body1"
        buttons={buttons}
        title="Esta es toda tu red de contactos."
      />
      <ContactsTable data={filteredContacts} />
      <NewContact open={openModal} setOpen={setOpenModal} />
    </Stack>
  );
};

export default Main;
